import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaBuilding, FaUser, FaPalette, FaEye, FaEyeSlash, FaCheck, FaSpinner, FaPlus, FaUsers, FaEdit, FaTrash } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

const Settings = ({ isOpen, onClose, salesAgents, onAgentExclude, analyticsData }) => {
  const [activeTab, setActiveTab] = useState('providers');
  const { theme, updateTheme } = useTheme();
  const [leadProviders, setLeadProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingProviders, setUpdatingProviders] = useState(new Set());
  const [successfulUpdates, setSuccessfulUpdates] = useState(new Set());
  
  // Teams state
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [newTeamForm, setNewTeamForm] = useState({
    teamName: '',
    managerName: '',
    salesAgents: []
  });
  const [addingTeam, setAddingTeam] = useState(false);
  
  // Add Provider Modal State
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [newProviderForm, setNewProviderForm] = useState({
    name: '',
    cost: '100.00',
    keywords: '',
    status: 'active'
  });
  const [addingProvider, setAddingProvider] = useState(false);
  
  // Add User Modal State
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Debounce function
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Fetch lead providers from API
  useEffect(() => {
    if (isOpen && activeTab === 'providers') {
      fetchLeadProviders();
    }
    if (isOpen && activeTab === 'teams') {
      fetchTeams();
    }
    if (isOpen && activeTab === 'agents') {
      fetchUsers();
    }
  }, [isOpen, activeTab]);

  const fetchLeadProviders = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/lead-providers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch lead providers');
      }

      const data = await response.json();
      setLeadProviders(data.providers || []);
    } catch (error) {
      console.error('Error fetching lead providers:', error);
      setError('Failed to load lead providers');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadProvider = async (providerName, newCost) => {
    try {
      setUpdatingProviders(prev => new Set([...prev, providerName]));
      setSuccessfulUpdates(prev => {
        const newSet = new Set(prev);
        newSet.delete(providerName);
        return newSet;
      });

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/lead-providers/${encodeURIComponent(providerName)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cost_per_lead: parseFloat(newCost) || 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update lead provider');
      }

      // Show success indicator
      setSuccessfulUpdates(prev => new Set([...prev, providerName]));
      
      // Clear success indicator after 2 seconds
      setTimeout(() => {
        setSuccessfulUpdates(prev => {
          const newSet = new Set(prev);
          newSet.delete(providerName);
          return newSet;
        });
      }, 2000);

      // Update local state immediately for better UX
      setLeadProviders(prev => prev.map(provider => 
        provider.provider_name === providerName 
          ? { ...provider, cost_per_lead: parseFloat(newCost) || 0 }
          : provider
      ));

    } catch (error) {
      console.error('Error updating lead provider:', error);
      setError(`Failed to update ${providerName}: ${error.message}`);
    } finally {
      setUpdatingProviders(prev => {
        const newSet = new Set(prev);
        newSet.delete(providerName);
        return newSet;
      });
    }
  };

  // Debounced update function
  const debouncedUpdate = useCallback(
    debounce((providerName, newCost) => {
      updateLeadProvider(providerName, newCost);
    }, 1000), // Wait 1 second after user stops typing
    []
  );

  const createLeadProvider = async (providerData) => {
    try {
      setAddingProvider(true);
      const token = localStorage.getItem('accessToken');
      
      // Parse keywords from comma-separated string
      const keywords = providerData.keywords 
        ? providerData.keywords.split(',').map(k => k.trim()).filter(k => k)
        : [providerData.name.toLowerCase()];
      
      const response = await fetch(`${API_URL}/api/lead-providers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider_name: providerData.name,
          cost_per_lead: parseFloat(providerData.cost) || 100.00,
          status: providerData.status || 'active',
          keywords: keywords
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create lead provider');
      }

      // Refresh the data
      await fetchLeadProviders();
      
      // Reset form and close modal
      setNewProviderForm({
        name: '',
        cost: '100.00',
        keywords: '',
        status: 'active'
      });
      setShowAddProviderModal(false);
      setError(''); // Clear any previous errors
      
    } catch (error) {
      console.error('Error creating lead provider:', error);
      setError(`Failed to create provider: ${error.message}`);
    } finally {
      setAddingProvider(false);
    }
  };

  const createUser = async (userData) => {
    try {
      setAddingUser(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: userData.username,
          email: userData.email,
          password: userData.password,
          role: userData.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create user');
      }

      // Refresh users list
      await fetchUsers();

      // Reset form and close modal
      setNewUserForm({
        username: '',
        email: '',
        password: '',
        role: 'user'
      });
      setShowAddUserModal(false);
      setError(''); // Clear any previous errors
      
    } catch (error) {
      console.error('Error creating user:', error);
      setError(`Failed to create user: ${error.message}`);
    } finally {
      setAddingUser(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setError('');
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddProviderSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newProviderForm.name.trim()) {
      setError('Provider name is required');
      return;
    }
    
    if (!newProviderForm.cost || parseFloat(newProviderForm.cost) < 0) {
      setError('Valid cost per lead is required');
      return;
    }
    
    await createLeadProvider(newProviderForm);
  };

  const handleCloseAddProviderModal = () => {
    setShowAddProviderModal(false);
    setNewProviderForm({
      name: '',
      cost: '100.00',
      keywords: '',
      status: 'active'
    });
    setError(''); // Clear any errors when closing
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newUserForm.username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (!newUserForm.email.trim()) {
      setError('Email is required');
      return;
    }
    
    // Only validate password for new users
    if (!editingUser) {
      if (!newUserForm.password.trim()) {
        setError('Password is required');
        return;
      }
      
      if (newUserForm.password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
    }
    
    if (editingUser) {
      await updateUser(editingUser._id, newUserForm);
    } else {
      await createUser(newUserForm);
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      setAddingUser(true);
      const token = localStorage.getItem('accessToken');
      
      // Only send fields that can be updated (exclude password for now)
      const updateData = {
        email: userData.email,
        role: userData.role
      };
      
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update agent');
      }

      // Refresh users list
      await fetchUsers();

      // Reset form and close modal
      setNewUserForm({
        username: '',
        email: '',
        password: '',
        role: 'user'
      });
      setEditingUser(null);
      setShowAddUserModal(false);
      setError(''); // Clear any previous errors
      
    } catch (error) {
      console.error('Error updating agent:', error);
      setError(`Failed to update agent: ${error.message}`);
    } finally {
      setAddingUser(false);
    }
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setEditingUser(null);
    setNewUserForm({
      username: '',
      email: '',
      password: '',
      role: 'user'
    });
    setError(''); // Clear any errors when closing
  };

  const handleAddUser = () => {
    setShowAddUserModal(true);
  };

  const handleEditUser = (user) => {
    setNewUserForm({
      username: user.username,
      email: user.email,
      password: '', // Don't populate password for security
      role: user.role
    });
    setEditingUser(user);
    setShowAddUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete agent');
      }

      // Refresh users list
      await fetchUsers();
      
    } catch (error) {
      console.error('Error deleting agent:', error);
      setError(`Failed to delete agent: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  const getLeadSourceCost = (source) => {
    // Find matching provider from database
    const provider = leadProviders.find(p => 
      p.provider_name.toLowerCase() === source.toLowerCase() ||
      p.keywords?.some(keyword => source.toLowerCase().includes(keyword.toLowerCase()))
    );
    
    return provider ? provider.cost_per_lead : 100.00;
  };

  const handlePriceChange = (providerName, newPrice) => {
    // Update local state immediately for responsive UI
    setLeadProviders(prev => prev.map(provider => 
      provider.provider_name === providerName 
        ? { ...provider, cost_per_lead: parseFloat(newPrice) || 0 }
        : provider
    ));
    
    // Debounced API call
    debouncedUpdate(providerName, newPrice);
  };

  const handleAddProvider = () => {
    setShowAddProviderModal(true);
  };

  const handleThemeChange = (key, value) => {
    updateTheme({ [key]: value });
  };

  const handleAgentExclude = (agentName, excluded) => {
    onAgentExclude(agentName, excluded);
  };

  // Get all lead sources from analytics data
  const allLeadSources = analyticsData?.leadSources || [];

  // Teams API functions
  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      setError('');
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/teams`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }

      const data = await response.json();
      setTeams(data.teams || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setError('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const createTeam = async (teamData) => {
    try {
      setAddingTeam(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_name: teamData.teamName,
          manager_name: teamData.managerName,
          sales_agents: teamData.salesAgents
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create team');
      }

      await fetchTeams();
      
      setNewTeamForm({
        teamName: '',
        managerName: '',
        salesAgents: []
      });
      setShowAddTeamModal(false);
      setError('');
      
    } catch (error) {
      console.error('Error creating team:', error);
      setError(`Failed to create team: ${error.message}`);
    } finally {
      setAddingTeam(false);
    }
  };

  const updateTeam = async (teamId, teamData) => {
    try {
      setAddingTeam(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_name: teamData.teamName,
          manager_name: teamData.managerName,
          sales_agents: teamData.salesAgents
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update team');
      }

      await fetchTeams();
      
      setNewTeamForm({
        teamName: '',
        managerName: '',
        salesAgents: []
      });
      setShowAddTeamModal(false);
      setEditingTeam(null);
      setError('');
      
    } catch (error) {
      console.error('Error updating team:', error);
      setError(`Failed to update team: ${error.message}`);
    } finally {
      setAddingTeam(false);
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete team');
      }

      await fetchTeams();
      
    } catch (error) {
      console.error('Error deleting team:', error);
      setError(`Failed to delete team: ${error.message}`);
    }
  };

  // Team management handlers
  const handleAddTeam = () => {
    setEditingTeam(null);
    setNewTeamForm({
      teamName: '',
      managerName: '',
      salesAgents: []
    });
    setShowAddTeamModal(true);
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setNewTeamForm({
      teamName: team.team_name,
      managerName: team.manager_name,
      salesAgents: team.sales_agents || []
    });
    setShowAddTeamModal(true);
  };

  const handleAddTeamSubmit = async (e) => {
    e.preventDefault();
    
    if (!newTeamForm.teamName.trim()) {
      setError('Team name is required');
      return;
    }
    
    if (!newTeamForm.managerName.trim()) {
      setError('Manager name is required');
      return;
    }

    if (newTeamForm.salesAgents.length === 0) {
      setError('At least one sales agent is required');
      return;
    }
    
    if (editingTeam) {
      await updateTeam(editingTeam._id, newTeamForm);
    } else {
      await createTeam(newTeamForm);
    }
  };

  const handleCloseTeamModal = () => {
    setShowAddTeamModal(false);
    setEditingTeam(null);
    setNewTeamForm({
      teamName: '',
      managerName: '',
      salesAgents: []
    });
    setError('');
  };

  const handleAgentToggle = (agentName) => {
    setNewTeamForm(prev => ({
      ...prev,
      salesAgents: prev.salesAgents.includes(agentName)
        ? prev.salesAgents.filter(agent => agent !== agentName)
        : [...prev.salesAgents, agentName]
    }));
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="settings-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="settings-tabs">
          <button 
            className={`settings-tab ${activeTab === 'providers' ? 'active' : ''}`}
            onClick={() => setActiveTab('providers')}
          >
            <FaBuilding /> Lead Providers
          </button>
          <button 
            className={`settings-tab ${activeTab === 'teams' ? 'active' : ''}`}
            onClick={() => setActiveTab('teams')}
          >
            <FaUsers /> Teams
          </button>
          <button 
            className={`settings-tab ${activeTab === 'agents' ? 'active' : ''}`}
            onClick={() => setActiveTab('agents')}
          >
            <FaUser /> Agents
          </button>
          <button 
            className={`settings-tab ${activeTab === 'styling' ? 'active' : ''}`}
            onClick={() => setActiveTab('styling')}
          >
            <FaPalette /> Styling
          </button>
        </div>

        <div className="settings-content">
          {activeTab === 'providers' && (
            <div className="settings-section">
              <div className="section-header">
                <h3>Lead Providers & Pricing</h3>
                <button className="add-provider-button" onClick={handleAddProvider}>
                  Add Provider
                </button>
              </div>
              
              {error && (
                <div className="error-message" style={{color: 'var(--danger)', marginBottom: '15px'}}>
                  {error}
                </div>
              )}

              {loading ? (
                <div style={{textAlign: 'center', padding: '20px'}}>Loading providers...</div>
              ) : (
                <>
                  {/* Database provider settings */}
                  <div className="provider-settings">
                    <h4>Provider Settings (Database)</h4>
                    <div className="providers-table-container">
                      <table className="providers-table">
                        <thead>
                          <tr>
                            <th>Provider Name</th>
                            <th>Cost per Lead</th>
                            <th>Keywords</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leadProviders.map((provider, index) => (
                            <tr key={index}>
                              <td className="provider-name-cell">{provider.provider_name}</td>
                              <td className="provider-cost-cell">
                                <div className="cost-input-container">
                                  <span className="currency-symbol">$</span>
                                  <input
                                    type="number"
                                    value={provider.cost_per_lead}
                                    onChange={(e) => handlePriceChange(provider.provider_name, e.target.value)}
                                    step="0.01"
                                    min="0"
                                    className="cost-input"
                                    style={{
                                      width: '80px',
                                      padding: '4px 8px 4px 20px',
                                      border: '1px solid var(--gray-300)',
                                      borderRadius: '4px',
                                      fontSize: '0.9rem',
                                      backgroundColor: updatingProviders.has(provider.provider_name) ? '#f0f8ff' : 'white'
                                    }}
                                  />
                                  <div className="update-status">
                                    {updatingProviders.has(provider.provider_name) && (
                                      <FaSpinner className="spinner" style={{ color: '#4c51bf', marginLeft: '8px' }} />
                                    )}
                                    {successfulUpdates.has(provider.provider_name) && (
                                      <FaCheck className="success-check" style={{ color: '#10b981', marginLeft: '8px' }} />
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="provider-keywords-cell">
                                {provider.keywords?.join(', ') || 'None'}
                              </td>
                              <td className="provider-status-cell">
                                <span className="status-active">{provider.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'teams' && (
            <div className="settings-section">
              <div className="section-header">
                <h3>Team Management</h3>
                <button className="add-team-button" onClick={handleAddTeam}>
                  Add Team
                </button>
              </div>
              
              {error && (
                <div className="error-message" style={{color: 'var(--danger)', marginBottom: '15px'}}>
                  {error}
                </div>
              )}

              {loadingTeams ? (
                <div style={{textAlign: 'center', padding: '20px'}}>Loading teams...</div>
              ) : (
                <div className="teams-container">
                  {teams.length === 0 ? (
                    <div className="no-teams">
                      <p>No teams created yet. Create your first team to start grouping agents.</p>
                    </div>
                  ) : (
                    <div className="teams-grid">
                      {teams.map((team, index) => (
                        <div key={team._id || index} className="team-card">
                          <div className="team-header">
                            <h4>{team.team_name}</h4>
                            <div className="team-actions">
                              <button 
                                className="edit-team-btn"
                                onClick={() => handleEditTeam(team)}
                                title="Edit Team"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="delete-team-btn"
                                onClick={() => deleteTeam(team._id)}
                                title="Delete Team"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                          <div className="team-details">
                            <div className="manager-info">
                              <strong>Manager:</strong> {team.manager_name}
                            </div>
                            <div className="agents-info">
                              <strong>Agents ({team.sales_agents?.length || 0}):</strong>
                              <div className="agents-list">
                                {team.sales_agents?.map((agent, agentIndex) => (
                                  <span key={agentIndex} className="agent-tag">
                                    {agent}
                                  </span>
                                )) || <span className="no-agents">No agents assigned</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'agents' && (
            <div className="settings-section">
              <div className="section-header">
                <h3>Active Agents</h3>
                <button className="add-team-button" onClick={handleAddUser}>
                  Create New User
                </button>
              </div>
              <div className="agents-table-container">
                <table className="agents-table">
                  <thead>
                    <tr>
                      <th>Agent Name</th>
                      <th>Closing Rate</th>
                      <th>Booked</th>
                      <th>Contacted</th>
                      <th>Cost per Lead</th>
                      <th>Total Cost</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesAgents?.map(agent => (
                      <tr key={agent.sales_agent} className={agent.excluded ? 'excluded-row' : ''}>
                        <td className="agent-name-cell">{agent.sales_agent}</td>
                        <td className="percentage-cell">{agent.closing_percentage}%</td>
                        <td className="number-cell">{agent.booked_count}</td>
                        <td className="number-cell">{agent.contacted_count}</td>
                        <td className="currency-cell">${agent.cost_per_user}</td>
                        <td className="currency-cell">${agent.total_lead_cost}</td>
                        <td className="action-cell">
                          <button 
                            className={`exclude-button ${agent.excluded ? 'excluded' : ''}`}
                            onClick={() => handleAgentExclude(agent.sales_agent, !agent.excluded)}
                            title={agent.excluded ? "Include in KPI" : "Exclude from KPI"}
                          >
                            {agent.excluded ? <FaEyeSlash /> : <FaEye />}
                            <span className="button-text">
                              {agent.excluded ? 'Excluded' : 'Active'}
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!salesAgents || salesAgents.length === 0) && (
                  <div className="no-agents">
                    <p>No agents data available</p>
                  </div>
                )}
              </div>

              {/* Users Section */}
              <div className="section-header" style={{marginTop: '2rem'}}>
                <h3>System Agents</h3>
              </div>
              
              {loadingUsers ? (
                <div style={{textAlign: 'center', padding: '20px'}}>Loading agents...</div>
              ) : (
                <div className="users-table-container">
                  <table className="agents-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users?.map(user => (
                        <tr key={user._id || user.username}>
                          <td className="agent-name-cell">{user.username}</td>
                          <td className="agent-name-cell">{user.email}</td>
                          <td className="agent-name-cell">
                            <span className={`role-badge ${user.role}`}>
                              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                            </span>
                          </td>
                          <td className="agent-name-cell">
                            <span className={`status-badge ${user.status || 'active'}`}>
                              {(user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1)}
                            </span>
                          </td>
                          <td className="agent-name-cell">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="action-cell">
                            <div className="user-actions">
                              <button 
                                className="edit-user-btn"
                                onClick={() => handleEditUser(user)}
                                title="Edit Agent"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="delete-user-btn"
                                onClick={() => handleDeleteUser(user._id)}
                                title="Delete Agent"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!users || users.length === 0) && (
                    <div className="no-agents">
                      <p>No agents created yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'styling' && (
            <div className="settings-section">
              <h3>Theme Settings</h3>
              <div className="styling-options">
                <div className="styling-option">
                  <label>Primary Color</label>
                  <input 
                    type="color" 
                    value={theme.primaryColor}
                    onChange={(e) => handleThemeChange('primaryColor', e.target.value)}
                  />
                </div>
                <div className="styling-option">
                  <label>Secondary Color</label>
                  <input 
                    type="color" 
                    value={theme.secondaryColor}
                    onChange={(e) => handleThemeChange('secondaryColor', e.target.value)}
                  />
                </div>
                <div className="styling-option">
                  <label>Font Size</label>
                  <select 
                    value={theme.fontSize}
                    onChange={(e) => handleThemeChange('fontSize', e.target.value)}
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div className="styling-option">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={theme.darkMode}
                      onChange={(e) => handleThemeChange('darkMode', e.target.checked)}
                    /> Enable Dark Mode
                  </label>
                </div>
                <div className="styling-option">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={theme.compactView}
                      onChange={(e) => handleThemeChange('compactView', e.target.checked)}
                    /> Compact View
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <button className="settings-save" onClick={onClose}>Save Changes</button>
          <button className="settings-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {/* Add Provider Modal */}
      {showAddProviderModal && (
        <div className="add-provider-overlay" onClick={handleCloseAddProviderModal}>
          <div className="add-provider-modal" onClick={e => e.stopPropagation()}>
            <div className="add-provider-header">
              <h3><FaPlus /> Add New Provider</h3>
              <button className="close-button" onClick={handleCloseAddProviderModal}>
                <FaTimes />
              </button>
            </div>
            
            {error && (
              <div className="error-message" style={{color: 'var(--danger)', marginBottom: '15px'}}>
                {error}
              </div>
            )}
            
            <form onSubmit={handleAddProviderSubmit} className="add-provider-form">
              <div className="form-group">
                <label htmlFor="provider-name">Provider Name *</label>
                <input
                  id="provider-name"
                  type="text"
                  value={newProviderForm.name}
                  onChange={(e) => setNewProviderForm({ ...newProviderForm, name: e.target.value })}
                  placeholder="e.g., Google Ads, Facebook Leads"
                  required
                  disabled={addingProvider}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="provider-cost">Cost per Lead *</label>
                <div className="cost-input-wrapper">
                  <span className="currency-symbol">$</span>
                  <input
                    id="provider-cost"
                    type="number"
                    value={newProviderForm.cost}
                    onChange={(e) => setNewProviderForm({ ...newProviderForm, cost: e.target.value })}
                    placeholder="100.00"
                    step="0.01"
                    min="0"
                    required
                    disabled={addingProvider}
                    className="form-input cost-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="provider-keywords">Keywords (comma-separated)</label>
                <input
                  id="provider-keywords"
                  type="text"
                  value={newProviderForm.keywords}
                  onChange={(e) => setNewProviderForm({ ...newProviderForm, keywords: e.target.value })}
                  placeholder="google, ads, ppc, search"
                  disabled={addingProvider}
                  className="form-input"
                />
                <small className="help-text">Used for matching lead sources. Leave empty to auto-generate.</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="provider-status">Status</label>
                <select
                  id="provider-status"
                  value={newProviderForm.status}
                  onChange={(e) => setNewProviderForm({ ...newProviderForm, status: e.target.value })}
                  disabled={addingProvider}
                  className="form-select"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseAddProviderModal}
                  className="cancel-button"
                  disabled={addingProvider}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={addingProvider || !newProviderForm.name.trim()}
                >
                  {addingProvider ? (
                    <>
                      <FaSpinner className="spinner" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      Add Provider
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Team Modal */}
      {showAddTeamModal && (
        <div className="add-team-overlay" onClick={handleCloseTeamModal}>
          <div className="add-team-modal" onClick={e => e.stopPropagation()}>
            <div className="add-team-header">
              <h3>
                <FaUsers /> {editingTeam ? 'Edit Team' : 'Add New Team'}
              </h3>
              <button className="close-button" onClick={handleCloseTeamModal}>
                <FaTimes />
              </button>
            </div>
            
            {error && (
              <div className="error-message" style={{color: 'var(--danger)', marginBottom: '15px'}}>
                {error}
              </div>
            )}
            
            <form onSubmit={handleAddTeamSubmit} className="add-team-form">
              <div className="form-group">
                <label htmlFor="team-name">Team Name *</label>
                <input
                  id="team-name"
                  type="text"
                  value={newTeamForm.teamName}
                  onChange={(e) => setNewTeamForm({ ...newTeamForm, teamName: e.target.value })}
                  placeholder="e.g., Sales Team Alpha, West Coast Team"
                  required
                  disabled={addingTeam}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="manager-name">Manager Name *</label>
                <input
                  id="manager-name"
                  type="text"
                  value={newTeamForm.managerName}
                  onChange={(e) => setNewTeamForm({ ...newTeamForm, managerName: e.target.value })}
                  placeholder="e.g., John Smith, Sarah Johnson"
                  required
                  disabled={addingTeam}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Sales Agents *</label>
                <div className="agents-selection">
                  {salesAgents && salesAgents.length > 0 ? (
                    <div className="agents-checkboxes">
                      {salesAgents.map((agent) => (
                        <div key={agent.sales_agent} className="agent-checkbox">
                          <input
                            type="checkbox"
                            id={`agent-${agent.sales_agent}`}
                            checked={newTeamForm.salesAgents.includes(agent.sales_agent)}
                            onChange={() => handleAgentToggle(agent.sales_agent)}
                            disabled={addingTeam}
                          />
                          <label htmlFor={`agent-${agent.sales_agent}`}>
                            {agent.sales_agent}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-agents-available">No agents available</p>
                  )}
                </div>
                <small className="help-text">
                  Selected: {newTeamForm.salesAgents.length} agent{newTeamForm.salesAgents.length !== 1 ? 's' : ''}
                </small>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseTeamModal}
                  className="cancel-button"
                  disabled={addingTeam}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={addingTeam || !newTeamForm.teamName.trim() || !newTeamForm.managerName.trim() || newTeamForm.salesAgents.length === 0}
                >
                  {addingTeam ? (
                    <>
                      <FaSpinner className="spinner" />
                      {editingTeam ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <FaUsers />
                      {editingTeam ? 'Update Team' : 'Create Team'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="add-user-overlay" onClick={handleCloseAddUserModal}>
          <div className="add-user-modal" onClick={e => e.stopPropagation()}>
            <div className="add-user-header">
              <h3><FaPlus /> {editingUser ? 'Edit Agent' : 'Add New Agent'}</h3>
              <button className="close-button" onClick={handleCloseAddUserModal}>
                <FaTimes />
              </button>
            </div>
            
            {error && (
              <div className="error-message" style={{color: 'var(--danger)', marginBottom: '15px'}}>
                {error}
              </div>
            )}
            
            <form onSubmit={handleAddUserSubmit} className="add-user-form">
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  id="username"
                  type="text"
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                  placeholder="e.g., john_doe"
                  required
                  disabled={addingUser || editingUser}
                  className="form-input"
                />
                {editingUser && (
                  <small className="help-text">Username cannot be changed when editing</small>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  placeholder="e.g., john@example.com"
                  required
                  disabled={addingUser}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                <input
                  id="password"
                  type="password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  placeholder={editingUser ? "Leave blank to keep current password" : "e.g., password123"}
                  required={!editingUser}
                  disabled={addingUser}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  disabled={addingUser}
                  className="form-select"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={handleCloseAddUserModal}
                  className="cancel-button"
                  disabled={addingUser}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={addingUser || !newUserForm.username.trim() || !newUserForm.email.trim() || (!editingUser && !newUserForm.password.trim())}
                >
                  {addingUser ? (
                    <>
                      <FaSpinner className="spinner" />
                      {editingUser ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <FaPlus />
                      {editingUser ? 'Update Agent' : 'Add Agent'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 